<template>
  <div class="bg-white rounded-xl shadow-sm">
    <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <i class="pi pi-pencil text-primary text-sm" />
        </div>
        <h3 class="text-sm font-semibold text-gray-800">Tests &amp; Exams</h3>
        <span v-if="activeTests.length > 0" class="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-primary/10 text-primary">
          {{ activeTests.length }} active
        </span>
      </div>
      <Button label="View all" icon="pi pi-arrow-right" iconPos="right" class="p-button-sm p-button-text" @click="goToCbt" />
    </div>

    <div class="p-4">
      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-6">
        <i class="pi pi-spin pi-spinner text-2xl text-primary" />
      </div>

      <!-- Empty -->
      <div v-else-if="!visibleTests.length" class="text-center py-8">
        <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <i class="pi pi-check text-gray-400" />
        </div>
        <p class="text-sm font-medium text-gray-700">No tests scheduled</p>
        <p class="text-xs text-gray-400 mt-0.5">You're all caught up.</p>
      </div>

      <!-- List -->
      <ul v-else class="space-y-2">
        <li
          v-for="test in visibleTests"
          :key="test.id"
          class="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-colors"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            :class="test.isActive ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'"
          >
            <i :class="test.isActive ? 'pi pi-play-circle' : 'pi pi-clock'" />
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-gray-800 truncate">{{ test.title }}</span>
              <span
                v-if="test.isActive"
                class="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary text-white"
              >Now</span>
            </div>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
              <span v-if="test.subject">{{ test.subject }}</span>
              <span class="flex items-center gap-1"><i class="pi pi-clock text-[10px]" />{{ test.duration }}m</span>
              <span class="flex items-center gap-1"><i class="pi pi-calendar text-[10px]" />{{ test.startsLabel }}</span>
            </div>
          </div>

          <Button
            v-if="test.isActive"
            label="Take test"
            icon="pi pi-arrow-right"
            iconPos="right"
            class="p-button-sm"
            @click="goToCbt"
          />
          <span v-else class="text-xs text-gray-400 whitespace-nowrap">{{ test.countdownLabel }}</span>
        </li>
      </ul>

      <p v-if="hiddenCount > 0" class="text-xs text-gray-400 mt-3 text-center">
        +{{ hiddenCount }} more — view all
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCBTTests } from '@/composables/queries/cbt/useCBTTest';

const router = useRouter();

const variables = computed(() => ({ first: 20, page: 1 }));
const { cbtTests } = useCBTTests({ autoFetch: true, variables });

const loading = cbtTests.loading;

const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now();
  }, 30_000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

interface TestRow {
  id: string;
  title: string;
  subject: string;
  duration: number;
  startMs: number;
  endMs: number | null;
  isActive: boolean;
  startsLabel: string;
  countdownLabel: string;
}

function describeQuestionable(q: Record<string, unknown> | null | undefined): string {
  if (!q) return '';
  if (q.__typename === 'Course') {
    return `${q.course_code ?? ''} ${q.course_title ?? ''}`.trim();
  }
  if (q.__typename === 'ClassroomSection') {
    const classroom = (q.classroom as Record<string, unknown> | null)?.name ?? '';
    return `${classroom} ${q.name ?? ''}`.trim();
  }
  return '';
}

function describeStartTime(startMs: number): string {
  const d = new Date(startMs);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return `Today, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function describeCountdown(startMs: number, currentMs: number): string {
  const diff = startMs - currentMs;
  if (diff <= 0) return 'Starting soon';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `In ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `In ${hours}h`;
  const days = Math.floor(hours / 24);
  return `In ${days}d`;
}

const rows = computed<TestRow[]>(() => {
  const data = cbtTests.data.value || [];
  return data.map((t: Record<string, unknown>) => {
    const startMs = t.start_time ? new Date(t.start_time as string).getTime() : 0;
    const endMs = t.end_time ? new Date(t.end_time as string).getTime() : null;
    const isActive = startMs <= now.value && (endMs === null || endMs >= now.value);
    return {
      id: t.id as string,
      title: (t.title as string) || 'Untitled test',
      subject: describeQuestionable(t.questionable as Record<string, unknown> | null | undefined),
      duration: (t.duration as number) || 0,
      startMs,
      endMs,
      isActive,
      startsLabel: describeStartTime(startMs),
      countdownLabel: describeCountdown(startMs, now.value),
    };
  });
});

const activeTests = computed(() => rows.value.filter((r) => r.isActive));

const upcomingTests = computed(() =>
  rows.value
    .filter((r) => !r.isActive && r.startMs > now.value)
    .sort((a, b) => a.startMs - b.startMs),
);

// Show active first, then nearest upcoming.
const visibleTests = computed(() => [...activeTests.value, ...upcomingTests.value].slice(0, 3));
const hiddenCount = computed(() =>
  Math.max(0, activeTests.value.length + upcomingTests.value.length - visibleTests.value.length),
);

const goToCbt = () => router.push({ name: 'student-cbt' });
</script>
